import React, { useEffect, useState } from 'react';

import { Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import ColoredButton from 'components/ColoredButton';
import general_messages from 'messages/general_messages';
import processing_activities_messages from 'messages/processing_activities_messages';
import validation_messages from 'messages/validation_messages';

type Props = {
  commentLabel?: string | string[];
  confirmLabel?: string | string[];
  helpLabel?: string | string[];
  initialComment?: string;
  loading?: boolean;
  onClose: () => void;
  onConfirm: (comment: string) => void | Promise<void>;
  open: boolean;
  titleLabel?: string | string[];
};

const RequestClarificationModal: React.FC<Props> = ({
  commentLabel,
  confirmLabel,
  helpLabel,
  initialComment = '',
  loading = false,
  onClose,
  onConfirm,
  open,
  titleLabel,
}) => {
  const { t } = useTranslation();
  const [comment, setComment] = useState(initialComment);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setComment(initialComment);
      setError('');
    }
  }, [initialComment, open]);

  const handleClose = () => {
    setComment('');
    setError('');
    onClose();
  };

  const handleConfirm = async () => {
    if (!comment.trim()) {
      setError(t(validation_messages.required));
      return;
    }

    await onConfirm(comment.trim());
  };

  return (
    <Dialog fullWidth maxWidth='sm' onClose={handleClose} open={open}>
      <DialogTitle>{t(titleLabel ?? processing_activities_messages.asset_ropa.modals.clarification.title)}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          error={Boolean(error)}
          fullWidth
          helperText={error || t(helpLabel ?? processing_activities_messages.asset_ropa.modals.clarification.help)}
          label={t(commentLabel ?? processing_activities_messages.asset_ropa.modals.clarification.comment)}
          multiline
          onChange={event => {
            setComment(event.target.value);
            if (error) setError('');
          }}
          rows={4}
          value={comment}
          variant='outlined'
        />
      </DialogContent>
      <DialogActions>
        <ColoredButton
          className=''
          customColor='grey'
          disabled={loading}
          icon={false}
          onClick={handleClose}
          showLoader={false}
          textLike={false}
          variant='outlined'
        >
          {t(general_messages.cancel)}
        </ColoredButton>
        <ColoredButton
          className=''
          customColor='secondary'
          disabled={loading}
          icon={false}
          onClick={handleConfirm}
          showLoader={loading}
          textLike={false}
          variant='contained'
        >
          {t(confirmLabel ?? processing_activities_messages.asset_ropa.modals.clarification.confirm)}
        </ColoredButton>
      </DialogActions>
    </Dialog>
  );
};

export default RequestClarificationModal;
